import {
  SchemaType,
  schemaTypeToJSON
} from "src/gen/schema/schema_path/schema_path";
import {
  Message,
  MonitoredObjects
} from "src/gen/schema/syncfollow/syncfollow";

import { StreamingCachePrintType } from "../dataflow/streamingCache";

import { Observable } from "./core/observable";
import { Socket } from "./core/socket";
import { Stream } from "./core/stream";
import { StreamReducer, StreamTransformer } from "./core/types";

type EVENT = "prune";
export abstract class StreamManager extends Observable<object> {
  static PRUNE: EVENT = "prune";
  protected abstract sock: Socket;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  protected streams: Map<string, Stream<Message, any>> = new Map();
  private url?: string;

  constructor() {
    super();
    this.addEvents([StreamManager.PRUNE]);
  }

  abstract handleMessage(m: MessageEvent<string | ArrayBuffer>): void;
  abstract handleReconnect(): void;
  abstract sendMessage(msg: Message): void;

  abstract addStream<TData>(
    message: Message,
    transformer: StreamTransformer<Message, TData>,
    reducer: StreamReducer<TData>
  ): Stream<Message, TData> | undefined;

  init(url: string) {
    this.connectSocket(url);
  }

  /**
   * Accepts a static URL or a builder function. Subclasses pass builders
   * so the WebSocket host is re-evaluated from `activeApiOrigin` on every
   * reconnect, adapting to Fred 307 redirects or cell-pair switchovers.
   */
  protected connectSocket(url: string | (() => string)) {
    const sock = this.sock;
    if (sock) {
      sock.un(Socket.MESSAGE, this.handleMessage, this);
      sock.un(Socket.RECONNECT, this.handleReconnect, this);
      if (url) {
        sock.init(url);
        sock.on(Socket.MESSAGE, this.handleMessage, this);
        sock.on(Socket.RECONNECT, this.handleReconnect, this);
        this.url = typeof url === "string" ? url : url();
      }
    }
  }

  close() {
    this.sock?.close();
  }

  isConnected(): boolean {
    return this.sock?.connected ?? false;
  }

  /**
   * Methods for Dev tools
   */
  getStreams = () => {
    return Array.from(this.streams.values());
  };

  getStream(id: string) {
    return this.streams.get(id);
  }
  // show active channels and what data is currently being monitored
  showContent = (): StreamingCachePrintType[] => {
    return this.getStreams().map((s) => {
      const messages = s.count;
      const req = s.getRequest();

      const requestObject: MonitoredObjects = req
        ?.objects[0] as unknown as MonitoredObjects;

      const schemaType = schemaTypeToJSON(
        requestObject?.types?.[0] || SchemaType.UNRECOGNIZED
      );

      const o: StreamingCachePrintType = {
        url: this.url || "",
        requestObject,
        schemaType,
        messages,
        observers: s.followers(),
        lastMessage: s.lastMessage,
        data: s.data,
        fabricId: requestObject?.fabricId?.[0],
        switchId: requestObject?.switchId?.[0],
        objectId: requestObject?.objectId?.[0]
      };
      return o;
    });
  };
}
